import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, List, ListItem,
  ListItemText, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { money, parseNumber, searchKey } from '../labels';
import { Loader, SectionTitle, useLoad, useNotify, useRun } from '../common';
import { isOpenTrip, tripTitle } from './tripTools';

const GROUP_KEY = 'planner.shoppingExpenseGroup';
const ACCOUNT_KEY = 'planner.shoppingExpenseAccount';

const readPref = (key: string) => { try { return window.localStorage.getItem(key) ?? ''; } catch { return ''; } };
const writePref = (key: string, value: string) => { try { window.localStorage.setItem(key, value); } catch { /* not available */ } };

interface PayData { trip: T.Trip; groups: T.ExpenseGroupOption[]; accounts: T.AccountOption[]; }

async function loadPayData(tripId: string): Promise<PayData> {
  const [trip, groups, accounts] = await Promise.all([
    api.getTrip(tripId),
    api.getExpenseGroups().catch(() => [] as T.ExpenseGroupOption[]),
    api.getExpenseAccounts().catch(() => [] as T.AccountOption[]),
  ]);
  return {
    trip,
    groups: groups.filter(g => !g.isIncomeGroup).sort((a, b) => a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' })),
    accounts: accounts.slice().sort((a, b) => Number(!!b.isShoppingDefault) - Number(!!a.isShoppingDefault)
      || Number(b.isPrimary) - Number(a.isPrimary) || a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' })),
  };
}

/**
 * Checkout: the receipt total is compared with the shelf prices; mismatching lines can be corrected one by one,
 * and the receipt total can be registered as a household expense.
 */
const PayTripPage: React.FC = () => {
  const { tripId = '' } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { data, error, loading, reload } = useLoad(() => loadPayData(tripId), [tripId]);
  const [receipt, setReceipt] = useState('');
  const [lines, setLines] = useState<Record<string, string>>({});
  const [withExpense, setWithExpense] = useState(true);
  const [groupId, setGroupId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState<T.MissingForMeal[] | null>(null);

  useEffect(() => {
    if (!data) return;
    setReceipt(data.trip.cartTotal.toFixed(2).replace('.', ','));
    // The group marked for shopping, else the last choice, else one that looks like groceries.
    // The account is the user's shopping account, else their default one, else the last choice; both can still be changed here.
    const lastGroup = readPref(GROUP_KEY);
    const group = data.groups.find(g => g.isShoppingDefault)
      ?? data.groups.find(g => g.publicId === lastGroup)
      ?? data.groups.find(g => searchKey(g.name).includes('supermercado'));
    setGroupId(group?.publicId ?? '');
    const lastAccount = readPref(ACCOUNT_KEY);
    // None of them: no account chosen (as on Android), so the expense is not filed under the wrong one
    setAccountId((data.accounts.find(a => a.isShoppingDefault) ?? data.accounts.find(a => a.isPrimary) ?? data.accounts.find(a => a.publicId === lastAccount))?.publicId ?? '');
  }, [data]);

  const cartLines = useMemo(() => (data?.trip.items ?? []).filter(i => i.state === 'InCart'), [data]);
  const receiptValue = parseNumber(receipt);
  const difference = data && receiptValue !== null ? receiptValue - data.trip.cartTotal : null;
  const matches = difference !== null && Math.abs(difference) <= 0.01;

  const pay = async () => {
    if (!data) return;
    if (receiptValue === null || receiptValue < 0) { setFormError('Indica o total do talão.'); return; }
    const receiptLines: T.ReceiptLineRequest[] = [];
    for (const line of cartLines) {
      const text = lines[line.publicId] ?? '';
      if (!text.trim()) continue;
      const total = parseNumber(text);
      if (total === null || total < 0) { setFormError('Há um valor de linha inválido.'); return; }
      receiptLines.push({ itemPublicId: line.publicId, total });
    }
    let expense: T.ExpenseRequest | null = null;
    if (withExpense) {
      if (!groupId || !accountId) { setFormError('Escolhe o grupo e a conta da despesa, ou desliga "Registar despesa".'); return; }
      expense = { groupPublicId: groupId, accountPublicId: accountId };
      writePref(GROUP_KEY, groupId);
      writePref(ACCOUNT_KEY, accountId);
    }
    setFormError(null);
    setBusy(true);
    try {
      const result = await api.payTrip(tripId, { receiptTotal: receiptValue, lines: receiptLines, expense });
      notify(result.expensePublicId ? 'Ida paga e despesa registada.' : 'Ida paga.', { severity: 'success' });
      if (result.missingForMeals.length > 0) setMissing(result.missingForMeals);
      else navigate(`/casa/idas/${tripId}`);
    } catch (e: any) {
      setFormError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Voltar" onClick={() => navigate(`/casa/idas/${tripId}`)} sx={{ mt: -0.5 }}><ArrowBackIcon /></IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Pagar</Typography>
          {data && <Typography variant="body2" color="text.secondary">{tripTitle(data.trip)}</Typography>}
        </Box>
      </Stack>

      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {data && !isOpenTrip(data.trip) && missing === null && (
          <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate(`/casa/idas/${tripId}`)}>Ver ida</Button>}>
            Esta ida já não está aberta.
          </Alert>
        )}
        {data && isOpenTrip(data.trip) && (
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <Typography variant="body2" color="text.secondary">
              Carrinho (preços da prateleira): {money(data.trip.cartTotal)}
            </Typography>
            <TextField
              label="Total do talão (€)" value={receipt} onChange={e => setReceipt(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            {difference !== null && (
              <Typography variant="body2" sx={{ color: matches ? 'success.main' : 'warning.main', fontWeight: 600 }}>
                {matches
                  ? '✔ Bate certo com o carrinho.'
                  : `⚠️ Diferença de ${difference > 0 ? '+' : ''}${money(difference)} face ao carrinho.`}
              </Typography>
            )}

            {difference !== null && !matches && cartLines.length > 0 && (
              <Box>
                <SectionTitle>Rever linha a linha</SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Escreve o valor do talão nas linhas que não batem certo com a prateleira (as outras podem ficar vazias).
                </Typography>
                <Paper variant="outlined">
                  <List disablePadding>
                    {cartLines.map(line => (
                      <ListItem key={line.publicId} divider sx={{ gap: 1 }}>
                        <ListItemText primary={line.name} secondary={money(line.lineTotal)} sx={{ minWidth: 0 }} />
                        <TextField
                          size="small" value={lines[line.publicId] ?? ''} placeholder={line.lineTotal.toFixed(2).replace('.', ',')}
                          onChange={e => setLines(l => ({ ...l, [line.publicId]: e.target.value }))}
                          slotProps={{ htmlInput: { inputMode: 'decimal', 'aria-label': `Valor no talão de ${line.name}` } }}
                          sx={{ width: 110, flexShrink: 0 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            <Box>
              <FormControlLabel
                control={<Switch checked={withExpense} onChange={e => setWithExpense(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Registar despesa</Typography>
                    <Typography variant="body2" color="text.secondary">Com o total do talão, nas despesas da casa.</Typography>
                  </Box>
                }
              />
            </Box>
            {withExpense && (
              <>
                <TextField select label="Grupo" value={groupId} onChange={e => setGroupId(e.target.value)}
                  helperText={data.groups.length === 0 ? 'Não há grupos de despesas da casa.' : undefined}>
                  {data.groups.map(g => <MenuItem key={g.publicId} value={g.publicId}>{g.name}</MenuItem>)}
                </TextField>
                <TextField select label="Conta" value={accountId} onChange={e => setAccountId(e.target.value)}
                  helperText={data.accounts.length === 0 ? 'Não há contas da casa.' : undefined}>
                  {data.accounts.map(a => <MenuItem key={a.publicId} value={a.publicId}>{a.name}</MenuItem>)}
                </TextField>
              </>
            )}

            {formError && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{formError}</Alert>}
            <Button variant="contained" size="large" onClick={pay} disabled={busy}>Pagar e fechar a ida</Button>
          </Stack>
        )}
      </Loader>

      <MissingDialog missing={missing} onClose={() => { setMissing(null); navigate(`/casa/idas/${tripId}`); }} />
    </>
  );
};

/** Ingredients for planned meals that did not come home: back on the list; buy elsewhere or make a task. */
function MissingDialog({ missing, onClose }: { missing: T.MissingForMeal[] | null; onClose: () => void }) {
  const navigate = useNavigate();
  const run = useRun();
  const [tasked, setTasked] = useState<string[]>([]);

  const createTask = async (m: T.MissingForMeal) => {
    if (await run(() => api.createNeedTask(m.needPublicId), `Tarefa «Comprar ${m.name}» criada.`))
      setTasked(t => t.concat(m.needPublicId));
  };

  return (
    <Dialog open={missing !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Faltam ingredientes</DialogTitle>
      <DialogContent>
        <List dense disablePadding>
          {(missing ?? []).map(m => (
            <ListItem key={m.needPublicId} disableGutters sx={{ flexWrap: 'wrap', gap: 1 }}>
              <ListItemText
                primary={`${m.name}${m.wasUnavailable ? ' (não havia)' : ''}`}
                secondary={m.meals.join(', ')}
                sx={{ flex: '1 1 180px' }}
              />
              <Button size="small" variant="outlined" disabled={tasked.includes(m.needPublicId)} onClick={() => createTask(m)}>
                {tasked.includes(m.needPublicId) ? 'Tarefa criada' : 'Criar tarefa para comprar'}
              </Button>
            </ListItem>
          ))}
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Voltaram para "Por comprar". Podes comprá-los noutra loja ou trocar a refeição na Semana.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={() => navigate('/casa/semana')}>Ver a semana</Button>
        <Button onClick={() => navigate('/casa/compras')}>Ver a lista</Button>
        <Button variant="contained" onClick={onClose}>OK</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PayTripPage;
